<template>
    <app-layout>
        <template #header>
            <AppHead title="Wholesale Checkout" />
            <ShopButton title="Wholesale Checkout" :link="back_to_shop_route || route('mtd.wholesale')" />
            <flash-messages />
        </template>

        <div v-if="cart_item_future_has_passed" class="mt-3">
            <p class="alert alert-info">
                We have removed some of your future delivered items because delivery date has passed.
            </p>
        </div>

        <section class="my-4">
            <div v-if="for_user" class="p-3">
                <strong>Ordering on behalf of {{ for_user.first_name }} {{ for_user.last_name }}</strong>
            </div>
            <div class="row">
                <div class="col-lg-6">
                    <card v-if="errors.length" headerStyle="danger" class="mb-3" icon="fas fa-times-circle"
                        :title="`${errors.length} ${errors.length > 1 ? 'errors' : 'error'} to fix`">
                        <ul class="mb-0 pl-4">
                            <li v-for="message in errors" :key="message">{{ message }}</li>
                        </ul>
                    </card>

                    <card title="Order Information" class="mb-3">
                        <div>
                            <label for="checkout_po_number">PO Number</label>
                            <input type="text" name="po_number" id="checkout_po_number" class="form-control form-control-sm"
                                v-model="form.po_number" />
                        </div>
                    </card>

                    <card v-if="has_shipping" title="Shipping Address">
                        <template v-slot:header>
                            <div v-if="$page.props.auth.user.mtd_use_customer_direct" class="form-check">
                                <input class="form-check-input" type="checkbox" id="customer-direct-destination-checkbox"
                                    v-model="formAddress.customer_direct_destination">
                                <label class="form-check-label" for="customer-direct-destination-checkbox">
                                    Customer Direct Destination
                                </label>
                            </div>
                        </template>

                        <div v-if="formAddress.customer_direct_destination">
                            <Multiselect v-model="form.ship_address_id" :options="filteredAddresses" :searchable="true"
                                :canDeselect="false" class="admin-multiselect" id="addresses_select" />
                        </div>
                        <div v-else>
                            <div class="address form-check" v-for="address in filteredAddresses" :key="address.value">
                                <input type="radio" name="ship_address" :id="'address_' + address.value"
                                    class="form-check-input" v-bind:value="address.value"
                                    @click="selectAddress($event, address)"
                                    :checked="address.value === form.ship_address_id" />
                                <label :for="'address_' + address.value">{{ address.label }}</label>
                                <div class="float-right">
                                    <button :disabled="addressProcessing" class="btn btn-link pt-0 text-secondary"
                                        @click="deleteAddress(address.value)">
                                        <i class="text-gray item-icon fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>

                            <jet-add-button @click="showAddress" modalTarget="#addressModal">Add Address</jet-add-button>
                        </div>

                        <div v-if="formAddress.customer_direct_destination">
                            <div class="float-right">
                                <button :disabled="addressProcessing" class="btn btn-link pt-0 text-secondary"
                                    @click="deleteAddress()">
                                    <i class="text-gray item-icon fas fa-trash"></i>
                                </button>
                            </div>

                            <jet-add-button @click="showAddress" modalTarget="#addressModal">Add Address</jet-add-button>
                        </div>
                    </card>

                    <card v-if="has_mtn_shipping" title="Shipping Method" class="mt-3">
                        <div v-if="has_mtn_shipping">
                            <div v-if="!form.ship_address_id"><span>Please select a shipping address</span></div>
                            <div v-else-if="methods.length === 0">
                                <i class="fa fa-spinner fa-spin"></i> Loading shipping rates...
                            </div>
                            <div v-else>
                                <div class="method form-check" v-for="method in methods" :key="method.id">
                                    <input type="radio" name="ship_method" class="form-check-input" :id="method.service_id"
                                        v-bind:value="method.id" :checked="method.service_id === shipping_method_id"
                                        @change="selectMethod($event, method)" />
                                    <label :for="method.service_id">
                                        <span v-if="method.cost > 0">{{ formatDollars(method.cost) }}</span>
                                        <span v-else>FREE</span> {{ method.label }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </card>

                    <div class="mt-3">
                        <PaymentMethod class="mt-4"
                            :mtd_instant_payment_only="$page.props.auth.user.mtd_instant_payment_only"
                            :payment_intents="payment_intents" :paymentBalance="cartTotals.balance"
                            :current_account_balance="cartTotals.current_account_balance" :form="form" :cards="cards"
                            :formBank="formBank" :banks="banks" :link_token="link_token" bankRoute="mtd.cart.addBank"
                            @setPaymentMethod="setPaymentMethod" @setPaymentIntent="setPaymentIntent"
                            @bankAdded="updateSelected" />
                    </div>

                    <card class="mt-2" bodyClasses="p-0">
                        <CheckoutTotals :cartTotals="cartTotals" :isWholesale="true" @onUpdate="load" />

                        <div class="d-flex justify-content-end mt-3 p-3">
                            <jet-button class="px-5" btnStyle="success" :disable="form.processing"
                                @click="completeCheckout()">Place Order</jet-button>
                        </div>
                    </card>

                    <card v-if="errors.length" headerStyle="danger" class="mb-3 mt-3" icon="fas fa-times-circle"
                        :title="`${errors.length} ${errors.length > 1 ? 'errors' : 'error'} to fix`">
                        <ul class="mb-0 pl-4">
                            <li v-for="message in errors" :key="message">{{ message }}</li>
                        </ul>
                    </card>

                    <card class="mb-3 mt-3 d-none d-md-block" title="Forget Something?" v-if="suggestions.length > 1">
                        <template v-for="product in suggestions" :key="product.id">
                            <ProductAdd :product="product" suggested_type="checkout" @onUpdate="load" />
                        </template>
                    </card>

                </div>

                <div class="col-lg-6 mt-3 mt-lg-0">
                    <CheckoutTable :items="items" :isWholesale="true" @onUpdate="load" />
                </div>
            </div>
        </section>

        <AddCard :routeUrl="route('mtd.cart.addCard')" :formCard="formCard" @cardUpdated="updateSelected" />

        <AddShippingAddress ref="addressForm" :states="states" @addAddress="addAddress" :formAddress="formAddress" />

        <jet-dialog-modal id="cashModal">
            <template #title>Cash Payment</template>
            <template #content>
                <i class="fa fa-spinner fa-spin"></i> Waiting for confirmation...
            </template>

            <template #footer>
                <jet-danger-button class="ml-2" @click="cancelCash">Cancel</jet-danger-button>
            </template>
        </jet-dialog-modal>

        <jet-dialog-modal id="termModal">
            <template #title>Payment Term Request</template>
            <template #content>
                <i class="fa fa-spinner fa-spin"></i> Waiting for confirmation...
            </template>

            <template #footer>
                <jet-danger-button class="ml-2" @click="cancelCash">Cancel</jet-danger-button>
            </template>
        </jet-dialog-modal>
    </app-layout>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout'
import FlashMessages from '@/Shared/FlashMessages'
import JetDialogModal from '@/Jetstream/DialogModal'
import JetDangerButton from '@/Jetstream/DangerButton'
import JetAddButton from '@/Jetstream/AddButton'
import ShopButton from '@/Shared/ShopButton'
import PaymentMethod from '@/Components/Shop/PaymentMethod'
import CheckoutTable from '@/Components/Shop/CheckoutTable'
import CheckoutTotals from '@/Components/Shop/CheckoutTotals'
import AddShippingAddress from '@/Components/Modals/AddShippingAddress'
import AddCard from '@/Components/Modals/AddCard'
import moment from 'moment'
import moment_timezone from 'moment-timezone'
import AppHead from '@/Shared/AppHead'
import Multiselect from '@vueform/multiselect'
import ProductAdd from '@/Components/Shop/ProductAdd'

export default {
    components: {
        AppLayout,
        FlashMessages,
        JetDialogModal,
        JetDangerButton,
        ShopButton,
        PaymentMethod,
        CheckoutTable,
        CheckoutTotals,
        AddShippingAddress,
        AddCard,
        JetAddButton,
        Multiselect,
        AppHead,
        ProductAdd,
    },
    props: {
        cart_item_future_has_passed: Boolean,
        items: Array,
        payment_intents: Array,
        payment_intent: String,
        payment_method_id: Number,
        cards: Array,
        banks: Array,
        link_token: String,
        back_to_shop_route: String,
        addresses: Array,
        ship_address: Object,
        for_user: Object,
        has_shipping: Boolean,
        has_mtn_shipping: Boolean,
        cartTotals: Array,
        states: Array,
        customer_direct_order: Boolean,
        suggestions: Array
    },
    data() {
        return {
            filteredAddresses: [],
            cartTotals: this.cartTotals,
            methods: [],
            shipping_method_id: null,
            errors: [],
            addressProcessing: false,
            form: this.$inertia.form({
                balance_displayed: this.cartTotals.balance,
                payment_amount: this.order_balance_total,
                payment_intent: this.payment_intent,
                stripe_payment_method: this.payment_method_id,
                term_id: null,
                ship_address_id: this.ship_address ? this.ship_address.id : null,
                ship_method: this.shipping_method_id,
                user_timezone: null,
                customer_direct_destination: false,
                po_number: '',
            }),
            formCard: this.$inertia.form({
                card_name: null,
                card_number: null,
                card_exp_month: null,
                card_exp_year: null,
                card_cvc: null,
                card_zip: null,
            }),
            formBank: this.$inertia.form({
                public_token: null,
                account_id: null
            }),
            formTermRequest: this.$inertia.form({
                term_id: null
            }),
            formAddress: this.$inertia.form({
                first_name: null,
                last_name: null,
                address1: null,
                address2: null,
                city: null,
                state: null,
                postcode: null,
                email: null,
                phone: null,
                company_name: null,
                customer_direct_destination: this.customer_direct_order,
            }),
            formUpdateAddress: this.$inertia.form({
                address_id: null
            }),
            formUpdateMethod: this.$inertia.form({
                method_id: null,
                cost: null
            }),
        }
    },
    created() {
        this.moment = moment
    },
    mounted() {
        this.filterAddresses()
        this.loadMethods()
    },
    watch: {
        'form.ship_address_id': function () {
            this.resetErrors();
            if (this.form.ship_address_id !== null && this.form.ship_address_id !== this.ship_address.id) {
                this.formUpdateAddress.address_id = this.form.ship_address_id;
                this.formUpdateAddress.post(this.route('mtd.cart.updateAddress'), {
                    onSuccess: () => {
                        this.loadMethods();
                        this.formAddress.customer_direct_destination = this.customer_direct_order;
                    }
                });
            }
        },
        'formAddress.customer_direct_destination': function () {
            this.filterAddresses();
        }
    },
    methods: {
        loadMethods() {
            if (this.has_mtn_shipping) {
                this.methods = [];
                let component = this;
                axios.get(route('mtd.cart.methods'), {}).then((response) => {
                    if (response.data.success) {
                        component.methods = response.data.methods;
                        component.shipping_method_id = response.data.shipping_method_id;
                        component.form.ship_method = response.data.shipping_method_id;
                        component.cartTotals = response.data.cartTotals;
                    }
                });
            }
        },
        load() {
            if (this.has_mtn_shipping) { this.loadMethods() }
            this.loadTotals()
        },
        loadTotals() {
            let component = this;
            axios.get(route('mtd.cart.totals'), {}).then((response) => {
                if (response.data.success) {
                    component.cartTotals = response.data.cartTotals;
                }
            });
        },
        resetErrors() {
            this.errors = [];
        },
        filterAddresses() {
            if (this.$page.props.auth.user.mtd_use_customer_direct) {
                let filteredAddresses = this.addresses.filter(address => address.is_customer_direct_destination == this.formAddress.customer_direct_destination);

                // If the selected shipment id is not in the new array, set it to null.
                if (!filteredAddresses.find(address => address.value === this.form.ship_address_id)) {
                    this.form.ship_address_id = null;
                    this.$nextTick(() => $('#addresses_select input').focus());
                }

                this.filteredAddresses = filteredAddresses;
            } else {
                this.filteredAddresses = this.addresses;
            }
        },
        selectAddress(e, address) {
            if (e.target.checked) this.form.ship_address_id = address.value;
        },
        async addAddress() {
            let { customer_direct_destination } = this.formAddress

            if (this.formAddress.phone) {
                this.formAddress.phone = this.formAddress.phone.replace(/[^0-9]+/g, '');
            }

            this.formAddress.post(this.route('mtd.cart.addAddress'), {
                onSuccess: () => {
                    $('#addressModal').modal('hide');
                    this.form.ship_address_id = this.ship_address.id;
                    this.filterAddresses();
                    this.loadMethods();
                    this.$refs.addressForm.$refs.address1.$el.value = null
                    this.formAddress.reset()
                    this.formAddress.customer_direct_destination = customer_direct_destination;
                }
            });
        },
        deleteAddress(shippingAddressId) {
            let addressId = typeof shippingAddressId !== "undefined" ? shippingAddressId : this.form.ship_address_id;
            this.addressProcessing = true
            this.$inertia.visit(this.route('cart.deleteAddress', addressId), {
                onSuccess: () => this.addressProcessing = false,
                onFinish: () => this.addressProcessing = false,
                preserveScroll: true
            })
        },
        showAddress() {
            this.resetErrors()
        },
        setPaymentMethod() {
            this.resetErrors();
        },
        selectMethod(e, method) {
            this.form.ship_method = method.service_id;
            this.formUpdateMethod.method_id = method.service_id;
            this.formUpdateMethod.cost = method.cost;

            this.formUpdateMethod.post(this.route('mtd.cart.updateMethod'), {
                onSuccess: () => {
                    this.loadTotals()
                }
            });
        },
        completeCheckout() {
            this.resetErrors();
            let canCheckout = false;
            if ((this.form.payment_intent === 'Credit Card' ||
                this.form.payment_intent === 'ACH') &&
                !this.form.stripe_payment_method) {
                this.errors[this.errors.length] = "Please select a payment method.";
            } else {
                canCheckout = true;
            }

            if (this.has_shipping) {
                if (!this.form.ship_address_id) {
                    this.errors[this.errors.length] = "Please select a shipping address.";
                    canCheckout = false;
                } else if (this.has_mtn_shipping) {
                    if (!this.form.ship_method) {
                        this.errors[this.errors.length] = "Please select a shipping method.";
                        canCheckout = false;
                    }
                }
            }

            if (canCheckout) {
                this.form.user_timezone = this.moment.tz.guess();

                this.form.balance_displayed = this.cartTotals.balance;
                this.form.post(this.route('mtd.cart.order'), {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.load();
                    }
                });
            }
        },
        setPaymentIntent(paymentIntent) {
            this.resetErrors()
            if (paymentIntent == this.form.payment_intent) return;
            this.form.payment_intent = paymentIntent;
            this.form.stripe_payment_method = null;
        },
        updateSelected() {
            this.form.stripe_payment_method = this.payment_method_id;
        },

        cancelCash() {
            $('#cashModal').modal('hide');
        },
    },
}
</script>
